import { PlusCircleSVG, TrashSVG } from 'common/assets';
import { SaleDepartmentEnum, EnumList } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { IconButton } from 'components/elements/buttons';
import Button from 'components/elements/buttons/button';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AirlineSelectInput from 'legacy-modules/master/airline/components/select-input';
import CruiseSelectInput from 'legacy-modules/master/cruise/components/select-input';
import HotelSelectInput from 'legacy-modules/master/hotel/components/select-input';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { CommissionFormType } from './form-type';
import { onAddNewCommissionDetail } from '../hooks/use-get-default-values';

const name = 'commissionMasterAdditionals' as const;

function CommissionDetail(props: { index: number; remove: () => void }) {
  const { t } = useTranslation();
  const { control, trigger } = useFormContext<CommissionFormType>();
  const parentName = `${name}.${props.index}` as const;
  const [field, department] = useWatch({
    control,
    name: [parentName, 'department'],
  });

  const { editable } = useFormState();
  const isPercentage = field.type === 'percentage';

  const commissionableSelect = React.useMemo(() => {
    const name = `${parentName}.commissionable`;
    switch (department) {
      case SaleDepartmentEnum.Flight:
        return <AirlineSelectInput name={name} />;
      case SaleDepartmentEnum.Hotel:
        return <HotelSelectInput name={name} />;
      case SaleDepartmentEnum.Cruise:
        return <CruiseSelectInput name={name} />;
      case SaleDepartmentEnum.Document:
        return (
          <EnumSelectInput
            label={t('common:document_type')}
            name={name}
            enumType={EnumList.DocumentType}
          />
        );
      case SaleDepartmentEnum.Activity:
        return (
          <EnumSelectInput
            label={t('common:activity_type')}
            name={name}
            enumType={EnumList.ActivityType}
          />
        );
      default:
        return null;
    }
  }, [department, parentName, t]);

  const deleteButtonComponent = editable && (
    <IconButton
      buttonType="tertiary"
      isError
      onClick={props.remove}
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
      }}
    >
      <TrashSVG color={stylesColors.redDefault} />
    </IconButton>
  );

  return (
    <div
      style={{
        position: 'relative',
        paddingTop: 16,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      {deleteButtonComponent}
      <SectionContainer>
        <HalfContainer>
          <Input
            type="select"
            name={`${parentName}.type`}
            label={t('common:type')}
            placeholder={t('common:type')}
            required
            clearable={false}
            options={[
              {
                id: 'fixed',
                label: t('common:fixed'),
              },
              {
                id: 'percentage',
                label: t('common:percentage'),
              },
            ]}
            onAfterChange={() => trigger(parentName)}
          />
        </HalfContainer>
        <HalfContainer>{commissionableSelect}</HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <Input
            type="number"
            name={`${parentName}.leadCommission`}
            label={t('invoice:lead')}
            placeholder={t('invoice:lead')}
            startEnhancer={isPercentage ? undefined : 'Rp. '}
            endEnhancer={isPercentage ? '%' : undefined}
            required
          />
        </HalfContainer>
        <HalfContainer>
          <Input
            type="number"
            name={`${parentName}.operatorSalesCommission`}
            label={t('invoice:operator_sales')}
            placeholder={t('invoice:operator_sales')}
            startEnhancer={isPercentage ? undefined : 'Rp. '}
            endEnhancer={isPercentage ? '%' : undefined}
            required
          />
        </HalfContainer>
      </SectionContainer>
    </div>
  );
}

export default function CommissionDetails() {
  const { t } = useTranslation();
  const { control, setValue, trigger } = useFormContext<CommissionFormType>();
  const { fields, append, remove } = useFieldArray({
    name,
    control,
    keyName: '__customId',
  });

  const onAdd = () => {
    append(onAddNewCommissionDetail());
  };
  const { editable } = useFormState();

  const addButtonComponent = editable && (
    <Button
      startEnhancer={(color, size) => (
        <PlusCircleSVG color={color} size={size} />
      )}
      onClick={onAdd}
    >
      {t('common:add')}
    </Button>
  );

  const onChangeCheckbox = React.useCallback(
    (value: boolean) => {
      if (!value) {
        setValue('type', null);
        setValue('leadCommission', null);
        setValue('operatorSalesCommission', null);
      }
    },
    [setValue],
  );

  const [type, hasBaseCommission, department] = useWatch({
    name: ['type', 'hasBaseCommission', 'department'],
  });

  const isPercentage = type === 'percentage';

  const hasAdditional = department !== SaleDepartmentEnum.Product;

  return (
    <>
      <CardContainer>
        <Text variant="LabelLarge">{t('common:general')}</Text>
        <Separator gap={16} />
        <HalfContainer>
          <Input
            type="select"
            name="type"
            label={t('common:type')}
            placeholder={t('common:type')}
            required
            clearable={false}
            options={[
              {
                id: 'fixed',
                label: t('common:fixed'),
              },
              {
                id: 'percentage',
                label: t('common:percentage'),
              },
            ]}
            disabled={!hasBaseCommission}
            onAfterChange={() =>
              trigger(['leadCommission', 'operatorSalesCommission'])
            }
          />
        </HalfContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="number"
              name="leadCommission"
              label={t('invoice:lead')}
              placeholder={t('invoice:lead')}
              startEnhancer={isPercentage ? undefined : 'Rp. '}
              endEnhancer={isPercentage ? '%' : undefined}
              required
              disabled={!hasBaseCommission}
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="number"
              name="operatorSalesCommission"
              label={t('invoice:operator_sales')}
              placeholder={t('invoice:operator_sales')}
              startEnhancer={isPercentage ? undefined : 'Rp. '}
              endEnhancer={isPercentage ? '%' : undefined}
              required
              disabled={!hasBaseCommission}
            />
          </HalfContainer>
        </SectionContainer>
        <HalfContainer>
          <Input
            type="check-box"
            label={t('common:has_base_commission')}
            name="hasBaseCommission"
            onAfterChange={onChangeCheckbox}
          />
        </HalfContainer>
      </CardContainer>
      <Separator gap={24} />
      {hasAdditional && (
        <>
          <CardContainer>
            <Text variant="LabelLarge">{t('common:additional')}</Text>
            <SeparatorVertical $margin={16} />
            {!fields.length && !editable && (
              <Text variant="ParagraphSmall">{t('common:no_data')}</Text>
            )}
            {fields.map((field, index) => {
              return (
                <CommissionDetail
                  key={field.customId}
                  index={index}
                  remove={() => {
                    remove(index);
                  }}
                />
              );
            })}

            {addButtonComponent}
          </CardContainer>
          <Separator gap={24} />
        </>
      )}
    </>
  );
}
